import { useCallback, type ReactNode } from 'react';
import { NavigationLink } from '../lib/navigationMemory';
import { cn } from '@/lib/utils';

export interface NavTab {
  id: string;
  label: ReactNode;
  to: string;
}

/**
 * The one tab bar of the console: hub tabs, sub-tabs and object-page tabs. Each tab is a link,
 * so the open tab lives in the URL and the back button works. `level="sub"` is the smaller
 * second row under a hub tab. On phones the row scrolls sideways (never the page) and the
 * active tab is scrolled into view.
 */
export function NavTabs({ tabs, active, label, level = 'main', className }: { tabs: readonly NavTab[]; active: string; label: string; level?: 'main' | 'sub'; className?: string }) {
  const centreActive = useCallback(
    (nav: HTMLElement | null) => {
      const a = nav?.querySelector<HTMLElement>('[aria-current="page"]');
      if (nav && a) nav.scrollLeft = a.offsetLeft - (nav.clientWidth - a.offsetWidth) / 2;
    },
    // Re-run when the active tab changes.
    // oxlint-disable-next-line react/exhaustive-deps
    [active],
  );
  if (level === 'sub') {
    return (
      <nav className={cn('mb-5 max-w-full overflow-x-auto', className)} aria-label={label} ref={centreActive}>
        <div className="inline-flex w-max gap-1 rounded-lg bg-muted p-1">
          {tabs.map((x) => (
            <NavigationLink
              key={x.id}
              to={x.to}
              aria-current={x.id === active ? 'page' : undefined}
              className={cn(
                'inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                x.id === active && 'bg-background text-foreground shadow-xs',
              )}
            >
              {x.label}
            </NavigationLink>
          ))}
        </div>
      </nav>
    );
  }
  return (
    <nav className={cn('mb-5 max-w-full overflow-x-auto border-b md:overflow-visible', className)} aria-label={label} ref={centreActive}>
      <div className="flex w-max gap-x-1 md:w-auto md:flex-wrap">
        {tabs.map((x) => (
          <NavigationLink
            key={x.id}
            to={x.to}
            aria-current={x.id === active ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-3 py-2.5 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              x.id === active && 'border-primary font-semibold text-primary',
            )}
          >
            {x.label}
          </NavigationLink>
        ))}
      </div>
    </nav>
  );
}
